import {
  ConfirmationDialog,
  DataGrid,
  StatusBadge,
  useNotifications
} from '@ijoin/components';
import { useGetAdminQueues, usePostAdminQueuesPurge } from '@ijoin/hooks';
import { DeleteSweepOutlined, InfoOutlined } from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { useState } from 'react';

import { LinkButton } from '../../components';
import {
  Health,
  HEALTH_LABEL,
  HEALTH_SEVERITY
} from '../../components/queues.utils';

type TuningHint = {
  code: string;
  message: string;
  severity: 'info' | 'warning';
};

type Queue = {
  activeIntegrations: number;
  backlogGrowthRate: number;
  consumerName: string;
  createdTimestamp: string;
  delayed: number;
  fairnessThrottling: boolean;
  hitsPerMinute: number;
  inFlight: number;
  isFifo: boolean;
  lastModifiedTimestamp: string;
  messages: number;
  name: string;
  queueCapMisses: number;
  queueUrl: string;
  retentionPeriod: number;
  tuningHints: TuningHint[];
  visibilityTimeout: number;
  worstHealth: Health;
  worstIntegrationName: null | string;
  worstSaturationPct: number;
};

const QUEUE_DETAILS: Record<string, string[]> = {
  'aggregate-billing-metrics': [
    'Aggregates monthly billing metrics for a single plan (planId + endOfMonth) via the server MetricsService.',
    'Queued by the NestJS server.'
  ],
  'aggregate-metrics': [
    'Recomputes aggregate metrics for one plan via the server MetricsService.',
    'Queued by the aggregate-plan-metrics cron after LDI calcs land, and by the server after a Relius plan sync, a v3 participant import or an enrollment save.'
  ],
  'annexus-file-processing': [
    'Annexus file jobs (AR004/RK001/RK002/RK003): builds CSVs per Annexus org and tracks AutoJobQueues rows.',
    'The producing cron is live, but the legacy consumer is commented out in profiles.js, so messages are not currently consumed.'
  ],
  'annuity-funding-transfer-relius-export': [
    'Legacy consumer submits an annuity funding transfer job to Relius via SOAP and polls job status, emailing support on failures and updating sweep details.',
    'Fed by the job router (ANNUITY_FUNDING_TRANSFER_EXPORT_TO_RELIUS).'
  ],
  'annuity-integration-in': [
    'Dead: no producer or consumer exists in the codebase.',
    'Only referenced by a TODO in the TIAA/SIA balance-file cron.'
  ],
  'automatic-forceout-distribution-relius-export': [
    'Legacy consumer sends a force-out distribution request to Relius via SOAP, polls job status, then marks RolloverParticipants as liquidation-requested and updates the AutoJobQueues job.',
    'Fed by the job router.'
  ],
  'automatic-forceout-processing': [
    'Legacy consumer identifies force-out-eligible participants for a plan and marks the job done.',
    'Fed by the job router and plan endpoints in the server.'
  ],
  'create-report': [
    'Legacy consumer builds reports: outside-assets reports POST to the NestJS server, others go through ReportsDao; job status tracked in AutoJobQueues.',
    'Fed by the job router (CREATE_REPORT).'
  ],
  'delete-advisory-organization': [
    'NestJS consumer cascade-deletes an advisory organization and its related rows (plan filters, attachments, export queues, integration plan metadata).'
  ],
  'delete-integration': [
    'NestJS consumer detaches an integration from its plans, deletes its RKO and advisory-org links and metadata rows, then the Integrations row itself.'
  ],
  'delete-plan': [
    'NestJS consumer cascade-deletes a plan and its dependent rows (survey responses, session rules, sync statuses, managed-account portfolios, investment vehicles, ...).'
  ],
  'delete-plan-enrollment': [
    'NestJS consumer deletes a plan enrollment and purges the participant once no enrollments remain.'
  ],
  'delete-record-keeper': [
    'NestJS consumer cascade-deletes a record keeping organization: RKO links, licenses, DataMine rows, SSO configs, then the organization row.'
  ],
  email: [
    'Legacy consumer renders an email template chosen from message metadata (type, language, whitelabel) and sends it.',
    'Produced by login flows, web sessions, and Relius integration code.'
  ],
  'expected-returns': [
    'Legacy consumer loads an expected-returns file from S3 into the database.',
    'No producer exists in the repo; nothing currently sends to this queue.'
  ],
  'external-metrics-migration': [
    'Runs the record-keeper external metrics migration for one month-end: clears the month, rebuilds MDMetrics and rolls them up into BillingMetrics via the server ExternalMetricsMigrationService.',
    'Queued by the metrics dashboard lock-in action (with source selections) and by the external-metrics-rollup cron (without, which runs the Omni + billing rollup only).'
  ],
  'ft-affiliated-funds-import': [
    'NestJS consumer loads the Franklin Templeton affiliated fund list CSV into FundUniverses, removing funds the snapshot no longer lists, then archives the file.',
    'Fed by an EventBridge rule on managed-account-methodologies/*/in/* in the sftp bucket; failures move the file to error/ and email support.'
  ],
  'generate-report': [
    'NestJS consumer generates MAP-fee and outside-assets CSV reports, tracking progress in AutoJobQueues.'
  ],
  job: [
    'Central job router (legacy consumer): marks the AutoJobQueues job in progress and re-sends it to the task-specific queue based on taskTypeId (syncs, MAP jobs, force-outs, sweeps, reports, notifications).',
    'Produced by JobQueueDao, the server autoJobQueue service, and several crons.'
  ],
  'ldi-calc': [
    'Legacy consumer runs LDI calculations for one participant or for every enrollment in a plan; per-participant failures fall back to snapshot updates, and it queues aggregate-metrics afterwards.',
    'Produced after participant imports and annuity funding transfers.'
  ],
  'map-allocation': [
    'Legacy consumer runs MAP analysis for a single managed-account participant.',
    'Produced by Stadion re-analysis, task schedules, plan endpoints, and MAP crons.'
  ],
  'map-auto-reenroll': [
    'Legacy consumer re-enrolls a participant into the managed-account program: re-syncs stale data from the record keeper, upserts ManagedAccountParticipants, and logs an AutoJobQueues row.',
    'Produced by the mapAutoReEnroll cron.'
  ],
  'map-fee-report-export': [
    'Legacy consumer builds the MAP fee XML and pushes fee transactions to Relius via SOAP, updating the job status.',
    'Fed by the job router.'
  ],
  'map-fee-schedules': [
    'Legacy consumer pulls fee schedules from Relius via SOAP for an integration and upserts them into ReliusFeeSchedules.',
    'Produced by the legacy api updateFeeSchedules endpoint.'
  ],
  'map-identify': [
    'Legacy consumer identifies new managed-account program entrants for a QDIA/MAP plan, optionally re-importing participants from Relius first.',
    'Fed by the job router.'
  ],
  'notification-send': [
    'Legacy consumer generates participant notification PDFs (beneficiary change, enrollment confirmation) and emails them, updating the job status.',
    'Fed by the job router.'
  ],
  'omni-export-ppt': [
    'Legacy consumer exports a participant to an OMNI record keeper (Alerus/Ameritas/NWPS), retrying until a valid result, then updates the export queue record.',
    'Produced by the pending-plan-integration-exports cron.'
  ],
  'omni-file-import': [
    'Legacy consumer runs OMNI plan and participant syncs across OMNI integrations.',
    'Produced by the omni-s3-file-import consumer for orgs not yet on the namespace-keyed SFTP pipeline.'
  ],
  'omni-plan-import': [
    'Legacy consumer imports one localized OMNI plan file via OmniDao.',
    'Produced by PlanIntegrationDao.'
  ],
  'omni-ppt-import': [
    'Legacy consumer imports one localized OMNI participant file via OmniDao.',
    'Produced by PlanIntegrationDao.'
  ],
  'omni-s3-file-import': [
    'NestJS consumer for S3 notifications on OMNI-style drops (Ameritas EXTRACT*, NWPS IJOIN*DATA, Alerus Ijoin*Setup, Alerus transaction CSVs).',
    'Routes CSVs to the Alerus transaction importer; other files are forwarded to omni-file-import (legacy syncs) or copied into the namespace-keyed SFTP bucket for the new pipeline.'
  ],
  'pcs-ppt-export': [
    'NestJS consumer exports a participant enrollment to PCS via the PCS API, updating RecordKeeperExportQueues status along the way.'
  ],
  'plan-import': [
    'Record-keeper sync pipeline: routed plan files are processed by a per-integration adapter (Alerus, Ameritas/FRP, Cetera, NWPS, PCS) into the plan tables, with AutoJobQueues tracking.',
    'Produced by the s3-file-import router.'
  ],
  'ppt-import': [
    'Record-keeper sync pipeline: routed participant files are processed by a per-integration adapter into the participant tables, with AutoJobQueues tracking.',
    'Produced by the s3-file-import router.'
  ],
  'pricing-import': [
    'NestJS consumer imports CIT price files dropped under the matrix namespace (EventBridge S3 events).'
  ],
  'relius-annuity-trans-file-import': [
    'Legacy consumer imports annuity transactions from an S3 file, then aggregates them per plan.',
    'Produced by the relius-file-import router.'
  ],
  'relius-export-ppt': [
    'Legacy consumer exports a participant to Relius via SOAP (regular or MAP re-analysis export) and kicks off status polling.',
    'Produced by the pending-plan-integration-exports cron.'
  ],
  'relius-export-ppt-status': [
    'Legacy consumer polls Relius job status for export jobs (retrying up to ~21 times), records warnings, and marks the export queue record completed or errored.'
  ],
  'relius-file-import': [
    'Legacy router for the Relius SFTP bucket: unpacks zip drops and routes files by folder to relius-plan-file-import, relius-ppt-file-import, or relius-annuity-trans-file-import.',
    'Produced by S3 bucket event notifications.'
  ],
  'relius-map-fee-pull': [
    'Gated. Legacy consumer pulls participant balances from Relius via SOAP for MAP fee calculation, writes feeData onto ManagedAccountParticipants, and merges fund balances into the enrollment document.',
    'Produced by the mapFeePull cron.'
  ],
  'relius-plan-file-import': [
    'Legacy consumer imports a plan JSON file from S3: creates a PLAN_SYNC job, sets up the plan if new, then runs importPlanConfig with the file data.',
    'Produced by the relius-file-import router.'
  ],
  'relius-plan-import': [
    'Gated. Legacy consumer runs a Relius SOAP plan-config sync for one plan, cancelling superseded jobs and archiving plans Relius reports as not iJoin-enabled.',
    'Fed by the job router (PLAN_SYNC).'
  ],
  'relius-plan-import-ondemand': [
    'Gated. On-demand variant of the Relius plan-config sync.',
    'Produced by the server relius service, the archived-plan reactivation cron, and PlanIntegrationDao.'
  ],
  'relius-ppt-file-import': [
    'Legacy consumer imports a participant JSON file from S3: creates a PARTICIPANT_SYNC job, imports via ReliusFileImportDao, then archives the file.',
    'Produced by the relius-file-import router.'
  ],
  'relius-ppt-import': [
    'Gated. Legacy consumer runs a scheduled Relius SOAP participant sync for one plan, deduping against newer jobs and updating AutoJobQueues.',
    'Fed by the job router (PARTICIPANT_SYNC).'
  ],
  'relius-ppt-import-ondemand': [
    'Gated. On-demand variant of the Relius SOAP participant sync.',
    'Fed by the job router when the job is flagged on-demand.'
  ],
  'relius-ppt-lite-import': [
    'Gated. NestJS consumer runs a lightweight Relius participant sync: pulls participant data over SOAP and upserts it, tracking progress and errors on the AutoJobQueues job.'
  ],
  's3-file-import': [
    'NestJS router for the namespace-keyed SFTP pipeline: receives EventBridge S3 events, dedupes redeliveries via a Redis claim, and routes each file to plan-import or ppt-import by filename pattern.'
  ],
  'send-email-campaign': [
    'NestJS consumer sends one campaign message: loads it with attachments, resolves the recipient enrollments, and sends batches through SendGrid.'
  ],
  'send-rollover-transactions-distribution-request': [
    'Legacy consumer submits rollover distribution transactions to Relius via SOAP and re-queues itself to poll job status until terminal.',
    'Produced by the Allianz annuity rollover cron.'
  ],
  'send-transactions-to-retirement-edge': [
    'Legacy consumer imports and aggregates annuity transactions for a plan, then sends them to Retirement Edge.',
    'Produced by the Allianz Relius transaction pull cron.'
  ],
  'social-security-override': [
    'NestJS consumer sets a social security calculation override (0.01) on participants matched by SSN or external plan id.'
  ],
  'straddle-sweep-funding-transfer-relius-export': [
    'Legacy consumer submits a straddle-sweep funding transfer to Relius via SOAP and polls job status, updating the AutoJobQueues job.',
    'Fed by the job router; upstream is the straddleSweepFundingTransfer cron.'
  ],
  'straddle-sweep-identify-map-balance-queue': [
    'NestJS consumer processes a straddle-sweep identify job from AutoJobQueues, skipping finished or superseded jobs, against the Relius integration.',
    'Fed by the job router and the NestJS server.'
  ],
  'transaction-aggregation': [
    'NestJS consumer aggregates participant record-keeper transactions for the requested funds and date range into a CSV and emails it to the requester.'
  ],
  'transaction-import': [
    'NestJS consumer receives EventBridge S3 events for transaction file drops, resolves the advisory org by S3 namespace, and imports the file via the Alerus service.'
  ],
  'update-plan-participant-snapshots': [
    'NestJS consumer recomputes one participant plan snapshot for a planCorrelationId + participantId.'
  ]
};

const QueuesPage = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const queues = useGetAdminQueues({
    query: { refetchInterval: 300_000 }
  });
  const purgeQueue = usePostAdminQueuesPurge({
    mutation: {
      onError: () => {
        notifications.error('Failed to purge queue');
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['/api/v3/admin/queues']
        });
        notifications.success(`Queue "${variables.data.name}" purged`);
      }
    }
  });
  const [pendingPurgeQueue, setPendingPurgeQueue] = useState<null | {
    consumerName: string;
    name: string;
  }>(null);

  const onCancelPurge = () => setPendingPurgeQueue(null);

  const onConfirmPurge = () => {
    if (pendingPurgeQueue) {
      purgeQueue.mutate({ data: { name: pendingPurgeQueue.consumerName } });
    }
    setPendingPurgeQueue(null);
  };

  const columns: GridColDef<Queue>[] = [
    {
      align: 'center',
      field: 'details',
      getActions: params => [
        <Tooltip
          arrow
          key='details'
          title={
            <Stack>
              {(
                QUEUE_DETAILS[params.row.name] ?? [
                  'No registered description for this queue.'
                ]
              ).map(line => (
                <Typography key={line} variant='body2'>
                  {line}
                </Typography>
              ))}
            </Stack>
          }>
          <GridActionsCellItem
            data-testid={`info-${params.row.name}`}
            icon={<InfoOutlined />}
            label='Details'
          />
        </Tooltip>
      ],
      headerName: '',
      sortable: false,
      type: 'actions',
      width: 50
    },
    {
      field: 'name',
      flex: 2,
      headerName: 'Name',
      minWidth: 220,
      renderCell: params => (
        <LinkButton
          params={{ name: params.row.name }}
          to='/queues/$name'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      align: 'right',
      field: 'messages',
      flex: 0.75,
      headerAlign: 'right',
      headerName: 'Messages',
      minWidth: 140,
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Messages</span>
          <Tooltip arrow title='Visible messages waiting in the queue.'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      align: 'right',
      field: 'inFlight',
      flex: 0.75,
      headerAlign: 'right',
      headerName: 'In Flight',
      minWidth: 130,
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>In Flight</span>
          <Tooltip
            arrow
            title='Messages received by a consumer but not yet acked or expired.'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      align: 'right',
      field: 'delayed',
      flex: 0.75,
      headerAlign: 'right',
      headerName: 'Delayed',
      minWidth: 130,
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Delayed</span>
          <Tooltip arrow title='Messages scheduled to become visible later.'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      align: 'right',
      field: 'activeIntegrations',
      flex: 0.75,
      headerAlign: 'right',
      headerName: 'Active Ints',
      minWidth: 140,
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Active Ints</span>
          <Tooltip
            arrow
            title='Integrations currently registered as active on this queue. Drained within ~30 seconds of the queue idling.'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      align: 'right',
      field: 'hitsPerMinute',
      flex: 0.75,
      headerAlign: 'right',
      headerName: 'Hits/min',
      minWidth: 130,
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Hits/min</span>
          <Tooltip
            arrow
            title='Successful gate acquisitions on this queue in the last minute. Pair with backlog growth to spot under-provisioned rate limits or fairness weights.'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      align: 'right',
      field: 'backlogGrowthRate',
      flex: 0.85,
      headerAlign: 'right',
      headerName: 'Backlog Δ',
      minWidth: 140,
      renderCell: params => {
        const value = params.value as number;

        if (!Number.isFinite(value) || Math.abs(value) < 0.05) {
          return '-';
        }

        const sign = value > 0 ? '+' : '';

        return `${sign}${value.toFixed(1)}/s`;
      },
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Backlog Δ</span>
          <Tooltip
            arrow
            title='Net change in queue depth per second over the last 2 minutes. Positive means the queue is filling faster than it drains.'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'fairnessThrottling',
      flex: 0.9,
      headerName: 'Fairness',
      minWidth: 140,
      renderCell: params => {
        if (!params.row.fairnessThrottling) {
          return <StatusBadge severity='success'>OK</StatusBadge>;
        }

        return (
          <Tooltip
            arrow
            title={`Per-queue cap blocked ${params.row.queueCapMisses} acquire(s) in the last minute.`}>
            <span>
              <StatusBadge severity='warning'>Throttled</StatusBadge>
            </span>
          </Tooltip>
        );
      },
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Fairness</span>
          <Tooltip
            arrow
            title='Throttled = the per-queue fairness cap blocked at least one acquire on this queue in the last minute (queue_cap miss). Other queues had pending work and this one was at its share.'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'worstHealth',
      flex: 1.25,
      headerName: 'Worst Health',
      minWidth: 170,
      renderCell: params => {
        if (!params.row.activeIntegrations) {
          return (
            <StatusBadge severity='success'>{HEALTH_LABEL.success}</StatusBadge>
          );
        }

        const tip = params.row.worstIntegrationName
          ? `${params.row.worstIntegrationName}: ${params.row.worstSaturationPct}% saturation`
          : `${params.row.worstSaturationPct}% saturation`;

        return (
          <Tooltip arrow title={tip}>
            <span>
              <StatusBadge severity={HEALTH_SEVERITY[params.value as Health]}>
                {HEALTH_LABEL[params.value as Health]}
              </StatusBadge>
            </span>
          </Tooltip>
        );
      },
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Worst Health</span>
          <Tooltip
            arrow
            title='Worst integration on this queue: Healthy / Pressure (≥75% sat with backlog) / Critical (≥90% sat with large backlog) / Idle (held but no in-flight).'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'isFifo',
      flex: 0.5,
      headerName: 'FIFO',
      minWidth: 110,
      renderCell: params => (
        <StatusBadge severity={params.value ? 'success' : 'info'}>
          {params.value ? 'Yes' : 'No'}
        </StatusBadge>
      ),
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>FIFO</span>
          <Tooltip
            arrow
            title='Whether the queue preserves message order (.fifo suffix).'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      align: 'right',
      field: 'visibilityTimeout',
      flex: 0.75,
      headerAlign: 'right',
      headerName: 'Visibility Timeout',
      minWidth: 180,
      renderHeader: () => (
        <Stack alignItems='center' direction='row'>
          <span>Visibility Timeout</span>
          <Tooltip
            arrow
            title='How long a received message stays hidden from other consumers before reappearing.'>
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
      valueFormatter: value => `${value}s`
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <Tooltip title='Purge queue'>
          <IconButton
            data-testid={`purge-${params.row.name}`}
            onClick={() =>
              setPendingPurgeQueue({
                consumerName: params.row.consumerName,
                name: params.row.name
              })
            }>
            <DeleteSweepOutlined />
          </IconButton>
        </Tooltip>
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.queueUrl}
        hideFooterPagination
        initialState={{
          sorting: {
            sortModel: [
              { field: 'inFlight', sort: 'desc' },
              { field: 'delayed', sort: 'desc' }
            ]
          }
        }}
        loading={queues.isFetching}
        pinnedColumns={{ left: ['details'], right: ['actions'] }}
        rows={queues.data ?? []}
        showColumnMenu
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'queues'
          }
        }}
        sx={{ flex: 1, minHeight: 0 }}
      />

      <ConfirmationDialog
        confirmationText='PURGE'
        confirmColor='error'
        confirmText='Purge'
        data-testid={
          pendingPurgeQueue ? `purge-${pendingPurgeQueue.name}` : 'purge'
        }
        isOpen={!!pendingPurgeQueue}
        message={
          pendingPurgeQueue
            ? `Are you sure you want to purge all messages from "${pendingPurgeQueue.name}"? This action cannot be undone.`
            : ''
        }
        onCancel={onCancelPurge}
        onConfirm={onConfirmPurge}
        title='Purge Queue'
      />
    </>
  );
};

export const Route = createFileRoute('/queues/')({
  component: QueuesPage
});
